import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HuitziMascot — el asistente colibrí mexicano (Códice Botánico).
 *
 * Inspiración: el colibrí como mensajero de Huitzilopochtli — símbolo de
 * salud y vitalidad. Rediseñado con anatomía real de colibrí (cuerpo en
 * gota, pico-aguja, alas falciformes, gorguera iridiscente, cola en
 * abanico) y la paleta Códice (jade, cempasúchil, jamaica, turquesa, oro).
 *
 * Estados (props.mood): idle · excited · thinking · happy · surprised · sleeping.
 * El componente solo renderiza el SVG y sus animaciones; la burbuja y el
 * chat viven en HuitziCompanion (wrapper).
 */
const JADE_DEEP    = '#143C28';
const JADE         = '#1F5C3D';
const JADE_BRIGHT  = '#2E8B57';
const TURQ         = '#2C7D8C';
const TURQ_LIGHT   = '#6FC3CE';
const CEMPA        = '#E0892E';
const COPAL        = '#B8531F';
const GOLD         = '#C9A227';
const JAMAICA      = '#8E2C42';
const JAMAICA_HI   = '#C0436A';
const INK          = '#20251C';
const CREAM        = '#EBE2CF';
const AGAVE        = '#8FA37E';

const wingFlapBySpeed = {
  idle: { duration: 0.32 },
  excited: { duration: 0.12 },
  thinking: { duration: 0.8 },
  happy: { duration: 0.18 },
  surprised: { duration: 1.4 },
  sleeping: { duration: 2.5 },
};

const bodyBobBySpeed = {
  idle: { y: [0, -4, 0], duration: 2.4 },
  excited: { y: [0, -8, 0], duration: 0.9 },
  thinking: { y: [0, -2, 0], duration: 3.6 },
  happy: { y: [0, -12, 0, -6, 0], duration: 1.1 },
  surprised: { y: [0, 4, 0], duration: 0.5 },
  sleeping: { y: [0, -1, 0], duration: 4.5 },
};

const HuitziMascot = ({
  mood = 'idle',
  size = 110,
  facing = 'right',
  onClick,
  ariaLabel = 'Huitzi, tu asistente colibrí',
}) => {
  const [blinking, setBlinking] = useState(false);

  // Random blink loop (skipped while sleeping)
  useEffect(() => {
    if (mood === 'sleeping') {
      setBlinking(true);
      return undefined;
    }
    let cancelled = false;
    const scheduleBlink = () => {
      const next = 2000 + Math.random() * 3500;
      setTimeout(() => {
        if (cancelled) return;
        setBlinking(true);
        setTimeout(() => {
          if (!cancelled) setBlinking(false);
          scheduleBlink();
        }, 140);
      }, next);
    };
    scheduleBlink();
    return () => { cancelled = true; };
  }, [mood]);

  const wing = wingFlapBySpeed[mood] || wingFlapBySpeed.idle;
  const bob = bodyBobBySpeed[mood] || bodyBobBySpeed.idle;
  const eyeOpenness = blinking ? 0.06 : (mood === 'surprised' ? 1.3 : 1);
  const headTilt = mood === 'thinking' ? -9 : mood === 'happy' ? 5 : 0;
  const flip = facing === 'left' ? -1 : 1;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      animate={{ y: bob.y }}
      transition={{ duration: bob.duration, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        width: size,
        height: size,
        background: 'transparent',
        border: 'none',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        display: 'inline-block',
      }}
    >
      <svg
        viewBox="0 0 120 120"
        width="100%"
        height="100%"
        style={{ overflow: 'visible', transform: `scaleX(${flip})`, filter: 'drop-shadow(0 6px 10px rgba(20,16,6,0.22))' }}
      >
        <defs>
          {/* Body: jade crown → jade → warm cempasúchil belly */}
          <linearGradient id="huBody" x1="20%" y1="0%" x2="60%" y2="100%">
            <stop offset="0%" stopColor={JADE_BRIGHT} />
            <stop offset="48%" stopColor={JADE} />
            <stop offset="100%" stopColor={CEMPA} />
          </linearGradient>
          {/* Head: jade with a touch of bright on top */}
          <radialGradient id="huHead" cx="40%" cy="30%" r="75%">
            <stop offset="0%" stopColor={JADE_BRIGHT} />
            <stop offset="100%" stopColor={JADE} />
          </radialGradient>
          {/* Near wing: turquoise iridescence with light leading edge */}
          <linearGradient id="huWingNear" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={TURQ_LIGHT} />
            <stop offset="55%" stopColor={TURQ} />
            <stop offset="100%" stopColor={JADE} />
          </linearGradient>
          {/* Far wing: deep jade */}
          <linearGradient id="huWingFar" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={JADE} />
            <stop offset="100%" stopColor={JADE_DEEP} />
          </linearGradient>
          {/* Tail: jade → deep */}
          <linearGradient id="huTail" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={JADE} />
            <stop offset="100%" stopColor={COPAL} />
          </linearGradient>
          {/* Gorget: iridescent jamaica */}
          <linearGradient id="huGorget" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={JAMAICA_HI} />
            <stop offset="100%" stopColor={JAMAICA} />
          </linearGradient>
          {/* Beak sheen */}
          <linearGradient id="huBeak" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={INK} />
            <stop offset="100%" stopColor="#3A4036" />
          </linearGradient>
        </defs>

        {/* Ground shadow */}
        <ellipse cx="58" cy="110" rx="24" ry="3.6" fill="rgba(20,16,6,0.16)" />

        {/* ---- Tail: fanned rectrices sweeping back-left ---- */}
        <motion.g
          animate={{ rotate: mood === 'happy' ? [0, 7, -5, 0] : [0, 2, 0] }}
          transition={{ duration: mood === 'happy' ? 0.8 : 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: '44px 76px' }}
        >
          <path d="M44 74 Q22 78 8 92 Q26 82 44 80 Z" fill="url(#huTail)" />
          <path d="M44 77 Q24 84 12 98 Q28 88 45 84 Z" fill={JADE_DEEP} opacity="0.92" />
          <path d="M45 80 Q30 88 24 102 Q34 92 46 88 Z" fill={COPAL} opacity="0.85" />
        </motion.g>

        {/* ---- Far wing (behind body) ---- */}
        <motion.g
          animate={{ rotate: [0, 40, 0, -40, 0] }}
          transition={{ duration: wing.duration, repeat: Infinity, ease: 'easeInOut', delay: 0.05 }}
          style={{ transformOrigin: '56px 60px' }}
        >
          {/* long sickle blade, swept up-left */}
          <path d="M56 60 Q34 40 14 44 Q30 52 38 62 Q30 60 24 64 Q40 66 56 64 Z"
            fill="url(#huWingFar)" opacity="0.6" />
        </motion.g>

        {/* ---- Body: sleek teardrop ---- */}
        <path
          d="M40 76 Q42 56 60 52 Q78 50 80 66 Q80 80 64 86 Q48 90 40 76 Z"
          fill="url(#huBody)"
        />
        {/* belly cream sheen */}
        <path d="M52 82 Q60 86 70 80 Q62 86 54 85 Z" fill={CREAM} opacity="0.35" />

        {/* ---- Head + tilt ---- */}
        <motion.g
          animate={{ rotate: headTilt }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          style={{ transformOrigin: '64px 50px' }}
        >
          {/* head */}
          <circle cx="66" cy="46" r="15" fill="url(#huHead)" />
          {/* iridescent crown sheen */}
          <path d="M54 40 Q62 31 78 36 Q66 35 56 43 Z" fill={GOLD} opacity="0.45" />

          {/* gorget — the iridescent throat patch (colibrí signature) */}
          <path d="M58 56 Q66 64 78 58 Q72 63 66 64 Q60 64 58 56 Z" fill="url(#huGorget)" />
          <path d="M62 58 Q66 61 71 59 Q67 61 64 61 Z" fill={JAMAICA_HI} opacity="0.7" />

          {/* beak — long downcurved needle */}
          <path d="M80 45 Q96 43 110 39 Q97 46 81 49 Z" fill="url(#huBeak)" />
          <path d="M80 45 Q96 43 110 39 Q100 42 82 46 Z" fill={CREAM} opacity="0.18" />

          {/* eye with catchlight */}
          <ellipse cx="71" cy="43" rx="3.8" ry={3.8 * eyeOpenness} fill={INK} />
          {!blinking && mood !== 'sleeping' && (
            <>
              <circle cx="72.4" cy="41.4" r="1.3" fill="white" />
              <circle cx="70" cy="44.2" r="0.7" fill="white" opacity="0.6" />
            </>
          )}

          {/* happy smile-line / surprised mouth */}
          {mood === 'happy' && (
            <path d="M62 60 Q66 64 71 60" stroke={JADE_DEEP} strokeWidth="1.4" fill="none" strokeLinecap="round" />
          )}
          {mood === 'surprised' && (
            <ellipse cx="66" cy="62" rx="2" ry="2.6" fill={JADE_DEEP} />
          )}
        </motion.g>

        {/* ---- Near wing (in front), flaps opposite ---- */}
        <motion.g
          animate={{ rotate: [0, -52, 0, 52, 0] }}
          transition={{ duration: wing.duration, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: '58px 62px' }}
        >
          {/* long falcate blade with feather split */}
          <path d="M58 62 Q36 44 12 50 Q30 58 40 68 Q28 66 20 72 Q42 74 58 68 Z"
            fill="url(#huWingNear)" opacity="0.92" />
          {/* leading-edge highlight */}
          <path d="M58 62 Q38 48 16 52 Q34 56 44 64 Z" fill={TURQ_LIGHT} opacity="0.5" />
        </motion.g>

        {/* tucked feet (subtle) */}
        <line x1="58" y1="87" x2="57" y2="93" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
        <line x1="63" y1="87" x2="64" y2="93" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />

        {/* Z's for sleeping */}
        <AnimatePresence>
          {mood === 'sleeping' && (
            <motion.g
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: -8 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              <text x="86" y="30" fontSize="14" fill={JADE} fontWeight="bold" fontFamily="serif">z</text>
              <text x="94" y="22" fontSize="10" fill={AGAVE} fontWeight="bold" fontFamily="serif">z</text>
            </motion.g>
          )}
        </AnimatePresence>

        {/* Sparkles for happy — codex gold + jamaica */}
        <AnimatePresence>
          {mood === 'happy' && (
            <motion.g
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [0, 1, 0], scale: [0.6, 1.2, 0.6] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            >
              <Sparkle x={28} y={34} c={GOLD} s={5} />
              <Sparkle x={96} y={62} c={JAMAICA_HI} s={4} />
              <Sparkle x={86} y={30} c={CEMPA} s={3.4} />
            </motion.g>
          )}
        </AnimatePresence>
      </svg>
    </motion.button>
  );
};

/* 4-point sparkle star */
const Sparkle = ({ x, y, c, s }) => (
  <path
    d={`M${x} ${y - s} Q${x + s * 0.25} ${y - s * 0.25} ${x + s} ${y} Q${x + s * 0.25} ${y + s * 0.25} ${x} ${y + s} Q${x - s * 0.25} ${y + s * 0.25} ${x - s} ${y} Q${x - s * 0.25} ${y - s * 0.25} ${x} ${y - s} Z`}
    fill={c}
  />
);

export default HuitziMascot;
